import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, RefreshCw, X, ChevronRight, TrendingUp, TrendingDown, Activity, ShieldAlert, AlertTriangle, Zap, Target, Eye } from "lucide-react";
import axios from "axios";
import Layout from "../components/custom/Layout";
import OctaPanel from "../components/custom/OctaPanel";
import { API } from "../contexts/AuthContext";
import { usePortfolioWS } from "../hooks/usePortfolioWS";

// ─── Universal symbol cleaner ───────────────────────────────────
function cleanSym(s) {
  if (!s) return s;
  for (const suffix of ["USDT", "PERP", ".NS", ".BSE", ".L", "-USD"]) {
    if (s.toUpperCase().endsWith(suffix)) return s.slice(0, -suffix.length);
  }
  return s;
}

// ─── Position Detail Panel ─────────────────────────────────────────
function PositionDetailPanel({ position, onClose }) {
  const [loading, setLoading] = useState(null);
  const [msg, setMsg] = useState("");
  const isProfit = position.pnl >= 0;

  const handleClose = async () => {
    setLoading("close");
    try {
      const { data } = await axios.post(`${API}/positions/${position._id}/close`);
      setMsg(data.message);
      setTimeout(onClose, 1500);
    } catch (e) { setMsg(e.response?.data?.detail || "Failed"); }
    finally { setLoading(null); }
  };

  const handleReduce = async (pct) => {
    setLoading(`reduce${pct}`);
    try {
      const { data } = await axios.post(`${API}/positions/${position._id}/reduce?percent=${pct}`);
      setMsg(data.message);
    } catch (e) { setMsg(e.response?.data?.detail || "Failed"); }
    finally { setLoading(null); }
  };

  const dataRows = [
    { label: "SIDE", value: position.side, color: position.side === "LONG" ? "#22C55E" : "#EF4444" },
    { label: "QUANTITY", value: position.quantity?.toString() },
    { label: "ENTRY", value: `$${position.entry_price?.toLocaleString()}` },
    { label: "MARK", value: `$${position.mark_price?.toLocaleString()}` },
    { label: "LEVERAGE", value: `${position.leverage}x`, color: "#38BDF8" },
    { label: "LIQ PRICE", value: `$${position.liquidation_price?.toLocaleString()}`, color: "#F59E0B" },
  ];

  return (
    <motion.div
      initial={{ x: "100%", opacity: 0 }}
      animate={{ x: 0, opacity: 1 }}
      exit={{ x: "100%", opacity: 0 }}
      transition={{ duration: 0.25, ease: "easeOut" }}
      className="w-[280px] flex-shrink-0 overflow-auto"
      style={{ background: "#0F1115", borderLeft: "1px solid rgba(255,255,255,0.04)" }}
      data-testid="position-detail-panel"
    >
      <div className="p-5">
        <div className="flex items-center justify-between mb-5">
          <div>
            <div style={{ fontFamily: "'Inter Tight', sans-serif", fontWeight: "700", color: "#E2E8F0", fontSize: "14px", letterSpacing: "0.04em" }}>{cleanSym(position.symbol)}</div>
            <div style={{ fontFamily: "'Inter Tight', sans-serif", fontSize: "9px", color: "#475569", letterSpacing: "0.14em", fontWeight: "500", marginTop: "2px", textTransform: "uppercase" }}>POSITION DETAIL</div>
          </div>
          <button onClick={onClose} data-testid="close-detail-btn" className="text-[#475569] hover:text-white transition-colors"><X size={15} /></button>
        </div>

        <div className="mb-5 p-4 relative" style={{
          clipPath: "polygon(8px 0%, calc(100% - 8px) 0%, 100% 8px, 100% calc(100% - 8px), calc(100% - 8px) 100%, 8px 100%, 0% calc(100% - 8px), 0% 8px)",
          background: "rgba(255,255,255,0.02)",
          border: `1px solid ${isProfit ? "rgba(34,197,94,0.12)" : "rgba(239,68,68,0.12)"}`,
        }}>
          <div style={{ fontFamily: "'JetBrains Mono', monospace", fontWeight: "700", fontSize: "22px", color: isProfit ? "#22C55E" : "#EF4444", fontVariantNumeric: "tabular-nums", lineHeight: 1.1 }}>
            {isProfit ? "+" : ""}{position.pnl?.toFixed(2)}
            <span style={{ fontSize: "11px", opacity: 0.5, marginLeft: "4px", fontWeight: "500" }}>PnL</span>
          </div>
          <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: "11px", color: isProfit ? "rgba(34,197,94,0.6)" : "rgba(239,68,68,0.6)", marginTop: "3px", fontVariantNumeric: "tabular-nums" }}>
            {isProfit ? "+" : ""}{Math.abs(position.unrealized_pnl_pct || 0).toFixed(2)}%
          </div>
        </div>

        <div className="mb-5">
          {dataRows.map(({ label, value, color }) => (
            <div key={label} className="flex justify-between items-center py-2.5" style={{ borderBottom: "1px solid rgba(255,255,255,0.03)" }}>
              <span style={{ fontFamily: "'Inter Tight', sans-serif", fontSize: "9px", letterSpacing: "0.14em", color: "#475569", fontWeight: "500" }}>{label}</span>
              <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: "12px", fontWeight: "600", color: color || "#E2E8F0", fontVariantNumeric: "tabular-nums" }}>{value}</span>
            </div>
          ))}
        </div>

        {msg && (
          <div className="mb-4 p-3 text-xs" style={{ fontFamily: "'JetBrains Mono', monospace", background: "rgba(34,197,94,0.06)", color: "#22C55E", clipPath: "polygon(5px 0%, calc(100% - 5px) 0%, 100% 5px, 100% calc(100% - 5px), calc(100% - 5px) 100%, 5px 100%, 0% calc(100% - 5px), 0% 5px)" }}>
            {msg}
          </div>
        )}

        <div style={{ fontFamily: "'Inter Tight', sans-serif", fontSize: "9px", letterSpacing: "0.16em", color: "#475569", fontWeight: "600", marginBottom: "10px", textTransform: "uppercase" }}>EXECUTE</div>
        <div className="space-y-2">
          {[25, 50].map((pct) => (
            <button key={pct} onClick={() => handleReduce(pct)} disabled={!!loading} data-testid={`reduce-${pct}-btn`}
              className="w-full transition-all duration-150 disabled:opacity-40"
              style={{ clipPath: "polygon(6px 0%, calc(100% - 6px) 0%, 100% 6px, 100% calc(100% - 6px), calc(100% - 6px) 100%, 6px 100%, 0% calc(100% - 6px), 0% 6px)", background: "rgba(245,158,11,0.06)", border: "1px solid rgba(245,158,11,0.2)", color: "#F59E0B", padding: "8px", fontFamily: "'Inter Tight', sans-serif", fontWeight: "600", fontSize: "10px", letterSpacing: "0.12em", textTransform: "uppercase" }}>
              {loading === `reduce${pct}` ? "..." : `REDUCE ${pct}%`}
            </button>
          ))}
          <button onClick={handleClose} disabled={!!loading} data-testid="close-position-btn"
            className="w-full transition-all duration-150 disabled:opacity-40"
            style={{ clipPath: "polygon(6px 0%, calc(100% - 6px) 0%, 100% 6px, 100% calc(100% - 6px), calc(100% - 6px) 100%, 6px 100%, 0% calc(100% - 6px), 0% 6px)", background: "rgba(239,68,68,0.06)", border: "1px solid rgba(239,68,68,0.25)", color: "#EF4444", padding: "10px", fontFamily: "'Inter Tight', sans-serif", fontWeight: "600", fontSize: "10px", letterSpacing: "0.12em", textTransform: "uppercase" }}>
            {loading === "close" ? "CLOSING..." : "CLOSE POSITION"}
          </button>
        </div>
      </div>
    </motion.div>
  );
}

// ─── Position Card ─────────────────────────────────────────────────
function PositionCard({ pos, isSelected, onSelect, delay }) {
  const isProfit = pos.pnl >= 0;
  const syncTime = pos.synced_at ? new Date(pos.synced_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : "--:--";

  return (
    <OctaPanel glowColor={isProfit ? "profit" : "loss"} onClick={() => onSelect(isSelected ? null : pos)} delay={delay} className="p-5">
      <div className="flex items-start justify-between mb-2">
        <div>
          <div style={{ fontFamily: "'Inter Tight', sans-serif", fontWeight: "700", color: "#E2E8F0", fontSize: "14px", letterSpacing: "0.04em" }}>{cleanSym(pos.symbol)}</div>
          <div className="flex items-center gap-1.5 mt-1">
            <span className="live-dot" style={{ display: "inline-block", width: "4px", height: "4px", borderRadius: "50%", background: "#22C55E" }} />
            <span style={{ fontFamily: "'Inter Tight', sans-serif", fontSize: "9px", color: "#22C55E", letterSpacing: "0.1em", fontWeight: "500" }}>LIVE</span>
          </div>
        </div>
        <span style={{ fontFamily: "'Inter Tight', sans-serif", fontWeight: "600", fontSize: "9px", letterSpacing: "0.12em", padding: "3px 8px", clipPath: "polygon(4px 0%, calc(100% - 4px) 0%, 100% 4px, 100% calc(100% - 4px), calc(100% - 4px) 100%, 4px 100%, 0% calc(100% - 4px), 0% 4px)", background: pos.side === "LONG" ? "rgba(34,197,94,0.08)" : "rgba(239,68,68,0.07)", color: pos.side === "LONG" ? "#22C55E" : "#EF4444" }}>
          {pos.side}
        </span>
      </div>
      <div style={{ height: "1px", background: "rgba(255,255,255,0.04)", margin: "12px 0 14px" }} />
      <div className="mb-1">
        <div style={{ fontFamily: "'JetBrains Mono', monospace", fontWeight: "700", fontSize: "26px", lineHeight: 1.1, color: isProfit ? "#22C55E" : "#EF4444", fontVariantNumeric: "tabular-nums", letterSpacing: "-0.01em" }}>
          {isProfit ? "+" : ""}{pos.pnl?.toFixed(2)}
          <span style={{ fontSize: "11px", opacity: 0.45, marginLeft: "5px", fontWeight: "500", letterSpacing: "0.05em" }}>PnL</span>
        </div>
        <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: "11px", color: isProfit ? "rgba(34,197,94,0.55)" : "rgba(239,68,68,0.55)", marginTop: "3px", fontVariantNumeric: "tabular-nums" }}>
          {isProfit ? "+" : ""}{Math.abs(pos.unrealized_pnl_pct || 0).toFixed(2)}%
        </div>
      </div>
      <div style={{ height: "1px", background: "rgba(255,255,255,0.04)", margin: "14px 0 12px" }} />
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: "8px" }}>
        {[
          { label: "ENTRY", value: `$${pos.entry_price?.toLocaleString()}`, color: "#94A3B8" },
          { label: "MARK", value: `$${pos.mark_price?.toLocaleString()}`, color: "#E2E8F0" },
          { label: "SIZE", value: pos.quantity, color: "#94A3B8" },
          { label: "LEV", value: `${pos.leverage}x`, color: "#38BDF8" },
        ].map(({ label, value, color }) => (
          <div key={label}>
            <div style={{ fontFamily: "'Inter Tight', sans-serif", fontSize: "8px", letterSpacing: "0.14em", color: "#334155", marginBottom: "4px", fontWeight: "500" }}>{label}</div>
            <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: "11px", fontWeight: "600", color, fontVariantNumeric: "tabular-nums" }}>{value}</div>
          </div>
        ))}
      </div>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: "14px", paddingTop: "10px", borderTop: "1px solid rgba(255,255,255,0.03)" }}>
        <span style={{ fontFamily: "'Inter Tight', sans-serif", fontSize: "8px", color: "#334155", letterSpacing: "0.1em", fontWeight: "500" }}>SYNCED {syncTime}</span>
        <ChevronRight size={11} style={{ color: "#334155" }} />
      </div>
    </OctaPanel>
  );
}

const CLIP_6 = "polygon(6px 0%, calc(100% - 6px) 0%, 100% 6px, 100% calc(100% - 6px), calc(100% - 6px) 100%, 6px 100%, 0% calc(100% - 6px), 0% 6px)";
const CLIP_8 = "polygon(8px 0%, calc(100% - 8px) 0%, 100% 8px, 100% calc(100% - 8px), calc(100% - 8px) 100%, 8px 100%, 0% calc(100% - 8px), 0% 8px)";
const MN = { fontFamily: "'JetBrains Mono', monospace", fontVariantNumeric: "tabular-nums" };
const IT = { fontFamily: "'Inter Tight', sans-serif" };
const LB = { fontFamily: "'Inter Tight', sans-serif", fontSize: "8px", letterSpacing: "0.16em", color: "#334155", fontWeight: "600" };

function pColor(v) { return v >= 70 ? "#22C55E" : v >= 55 ? "#38BDF8" : v >= 40 ? "#F59E0B" : "#EF4444"; }

// ─── Radial Factor Chart (unique to workspace — not in main grid) ──
function RadialFactorChart({ factors, size = 160 }) {
  const items = [
    { key: "trend", label: "TRD", max: 15 },
    { key: "volatility", label: "VOL", max: 10 },
    { key: "momentum", label: "MOM", max: 10 },
    { key: "exposure_concentration", label: "CON", max: 15 },
    { key: "leverage", label: "LEV", max: 10 },
    { key: "correlation", label: "COR", max: 10 },
    { key: "margin_pressure", label: "MAR", max: 10 },
    { key: "drawdown", label: "DDN", max: 10 },
  ];
  const cx = size / 2, cy = size / 2, maxR = size / 2 - 20;
  const angleStep = (2 * Math.PI) / items.length;

  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
      {/* Grid rings */}
      {[0.25, 0.5, 0.75, 1].map((r, i) => (
        <circle key={i} cx={cx} cy={cy} r={maxR * r} fill="none" stroke="rgba(255,255,255,0.03)" strokeWidth="0.5" />
      ))}
      {/* Grid lines */}
      {items.map((_, i) => {
        const angle = -Math.PI / 2 + i * angleStep;
        const x2 = cx + maxR * Math.cos(angle);
        const y2 = cy + maxR * Math.sin(angle);
        return <line key={i} x1={cx} y1={cy} x2={x2} y2={y2} stroke="rgba(255,255,255,0.03)" strokeWidth="0.5" />;
      })}
      {/* Data polygon */}
      {(() => {
        const pts = items.map((item, i) => {
          const val = (factors[item.key] || 0) / item.max;
          const angle = -Math.PI / 2 + i * angleStep;
          const r = maxR * val;
          return `${cx + r * Math.cos(angle)},${cy + r * Math.sin(angle)}`;
        }).join(" ");
        return (
          <>
            <polygon points={pts} fill="rgba(56,189,248,0.08)" stroke="#38BDF8" strokeWidth="1.5" strokeLinejoin="round" style={{ filter: "drop-shadow(0 0 4px rgba(56,189,248,0.2))" }} />
            {items.map((item, i) => {
              const val = (factors[item.key] || 0) / item.max;
              const angle = -Math.PI / 2 + i * angleStep;
              const r = maxR * val;
              const x = cx + r * Math.cos(angle);
              const y = cy + r * Math.sin(angle);
              const lx = cx + (maxR + 12) * Math.cos(angle);
              const ly = cy + (maxR + 12) * Math.sin(angle);
              const color = val >= 0.6 ? "#22C55E" : val >= 0.35 ? "#F59E0B" : "#EF4444";
              return (
                <g key={i}>
                  <circle cx={x} cy={y} r="3" fill={color} style={{ filter: `drop-shadow(0 0 3px ${color}40)` }} />
                  <text x={lx} y={ly} textAnchor="middle" dominantBaseline="middle" style={{ ...IT, fontSize: "7px", fontWeight: "600" }} fill="#475569">{item.label}</text>
                </g>
              );
            })}
          </>
        );
      })()}
    </svg>
  );
}

// ─── Platform Intelligence Tab ──────────────────────────────────────
function PlatformIntelligenceTab({ platformId }) {
  const [intel, setIntel] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetch = async () => {
      try {
        const { data } = await axios.get(`${API}/intelligence/platform/${platformId}`);
        setIntel(data);
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    };
    fetch();
    const iv = setInterval(fetch, 10000);
    return () => clearInterval(iv);
  }, [platformId]);

  if (loading) return <div className="animate-pulse space-y-4"><div className="h-48 bg-white/[0.02]" style={{ clipPath: CLIP_8 }} /></div>;
  if (!intel) return <div className="text-center py-16"><p style={{ ...IT, fontSize: "12px", color: "#475569" }}>Intelligence unavailable</p></div>;

  const prob = intel.probability || 0;
  const color = pColor(prob);
  const factors = intel.factor_breakdown || {};
  const regime = intel.regime || {};
  const exposure = intel.exposure_map || {};
  const topPos = intel.top_positions || [];
  const bias = exposure.directional_bias || {};

  return (
    <div className="space-y-5" data-testid="platform-intelligence-tab">
      {/* Top: Probability + Radial chart */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <OctaPanel delay={0.05} className="p-5">
          <div className="flex items-center gap-6">
            {/* Probability ring — unique half-arc style */}
            <div className="flex-shrink-0 relative" style={{ width: "110px", height: "110px" }}>
              <svg viewBox="0 0 110 110" width="110" height="110">
                <circle cx="55" cy="55" r="48" fill="none" stroke="rgba(255,255,255,0.03)" strokeWidth="5" />
                <motion.circle cx="55" cy="55" r="48" fill="none" stroke={color} strokeWidth="5" strokeLinecap="round"
                  strokeDasharray={2 * Math.PI * 48} initial={{ strokeDashoffset: 2 * Math.PI * 48 }}
                  animate={{ strokeDashoffset: 2 * Math.PI * 48 * (1 - prob / 100) }}
                  transition={{ duration: 1.2, ease: "easeOut" }}
                  style={{ filter: `drop-shadow(0 0 6px ${color}30)`, transform: "rotate(-90deg)", transformOrigin: "55px 55px" }} />
              </svg>
              <div className="absolute inset-0 flex flex-col items-center justify-center">
                <span style={{ ...MN, fontSize: "24px", fontWeight: "700", color }}>{prob.toFixed(0)}</span>
                <span style={{ ...LB, fontSize: "6px" }}>PROBABILITY</span>
              </div>
            </div>
            {/* Key metrics */}
            <div className="flex-1 space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div><div style={LB}>STABILITY</div><div style={{ ...IT, fontSize: "13px", fontWeight: "700", color }}>{intel.stability}</div></div>
                <div><div style={LB}>REGIME</div><div style={{ ...IT, fontSize: "13px", fontWeight: "700", color: "#38BDF8" }}>{regime.regime}</div></div>
                <div><div style={LB}>PRESSURE</div><div style={{ ...IT, fontSize: "11px", fontWeight: "600", color: "#F59E0B" }}>{intel.main_pressure}</div></div>
                <div><div style={LB}>MARGIN</div><div style={{ ...MN, fontSize: "13px", fontWeight: "700", color: intel.margin_pct > 50 ? "#F59E0B" : "#94A3B8" }}>{intel.margin_pct}%</div></div>
              </div>
              {/* Suggested action */}
              <div className="px-3 py-2" style={{ clipPath: CLIP_6, background: "rgba(56,189,248,0.03)", border: "1px solid rgba(56,189,248,0.08)" }}>
                <div className="flex items-start gap-2">
                  <Target size={10} style={{ color: "#38BDF8", flexShrink: 0, marginTop: "2px" }} />
                  <span style={{ fontFamily: "'IBM Plex Sans', sans-serif", fontSize: "10px", color: "#94A3B8", lineHeight: 1.4 }}>{intel.suggested_action}</span>
                </div>
              </div>
            </div>
          </div>
        </OctaPanel>

        {/* Radial factor chart — unique to workspace */}
        <OctaPanel delay={0.1} className="p-5">
          <div style={{ ...LB, marginBottom: "8px" }}>FACTOR ANALYSIS</div>
          <div className="flex items-center justify-center">
            <RadialFactorChart factors={factors} size={180} />
          </div>
        </OctaPanel>
      </div>

      {/* Exposure + Regime row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {/* Exposure donut-style */}
        <OctaPanel delay={0.15} className="p-5">
          <div style={{ ...LB, marginBottom: "10px" }}>EXPOSURE BREAKDOWN</div>
          {/* Direction bias bar */}
          <div className="mb-3">
            <div className="flex h-3 overflow-hidden" style={{ clipPath: CLIP_6 }}>
              <div style={{ width: `${bias.long_pct || 50}%`, background: "linear-gradient(90deg, #22C55E80, #22C55E)", transition: "width 0.6s" }} />
              <div style={{ width: `${bias.short_pct || 50}%`, background: "linear-gradient(90deg, #EF4444, #EF444480)", transition: "width 0.6s" }} />
            </div>
            <div className="flex justify-between mt-1">
              <span style={{ ...MN, fontSize: "9px", color: "#22C55E" }}>LONG {bias.long_pct}%</span>
              <span style={{ ...MN, fontSize: "9px", color: "#94A3B8", fontWeight: "600" }}>{bias.net_bias}</span>
              <span style={{ ...MN, fontSize: "9px", color: "#EF4444" }}>SHORT {bias.short_pct}%</span>
            </div>
          </div>
          {/* Asset bars with glow */}
          <div className="space-y-2">
            {(exposure.by_asset || []).slice(0, 5).map((a, i) => {
              const barColor = a.pnl >= 0 ? "#22C55E" : "#EF4444";
              return (
                <div key={i} className="flex items-center gap-2.5">
                  <span style={{ ...IT, fontSize: "9px", fontWeight: "600", color: "#94A3B8", width: "40px" }}>{a.display_name || cleanSym(a.symbol)}</span>
                  <div className="flex-1 h-2 overflow-hidden" style={{ clipPath: CLIP_6, background: "rgba(255,255,255,0.03)" }}>
                    <motion.div initial={{ width: 0 }} animate={{ width: `${Math.min(a.exposure_pct, 100)}%` }}
                      transition={{ duration: 0.8, delay: i * 0.08 }}
                      style={{ height: "100%", background: `linear-gradient(90deg, ${barColor}60, ${barColor})`, boxShadow: `0 0 6px ${barColor}20` }} />
                  </div>
                  <span style={{ ...MN, fontSize: "9px", color: barColor, width: "38px", textAlign: "right" }}>{a.exposure_pct}%</span>
                </div>
              );
            })}
          </div>
        </OctaPanel>

        {/* Regime + Volatility gauges */}
        <OctaPanel delay={0.2} className="p-5">
          <div style={{ ...LB, marginBottom: "10px" }}>REGIME STATE</div>
          <div className="flex items-center gap-4 mb-4">
            <div className="w-12 h-12 flex items-center justify-center flex-shrink-0" style={{
              clipPath: CLIP_6, background: `${pColor(prob)}0A`, border: `1px solid ${pColor(prob)}20`
            }}>
              <span style={{ ...IT, fontSize: "18px", fontWeight: "800", color: pColor(prob) }}>{regime.regime?.[0]}</span>
            </div>
            <div>
              <div style={{ ...IT, fontSize: "14px", fontWeight: "700", color: pColor(prob) }}>{regime.regime}</div>
              <div style={{ fontFamily: "'IBM Plex Sans', sans-serif", fontSize: "10px", color: "#475569", lineHeight: 1.4 }}>{regime.description}</div>
            </div>
          </div>
          <div className="grid grid-cols-3 gap-3 pt-3" style={{ borderTop: "1px solid rgba(255,255,255,0.04)" }}>
            <div className="text-center">
              <div style={{ ...IT, fontSize: "12px", fontWeight: "700", color: regime.volatility_state === "High" ? "#EF4444" : regime.volatility_state === "Moderate" ? "#F59E0B" : "#22C55E" }}>{regime.volatility_state}</div>
              <div style={LB}>VOLATILITY</div>
            </div>
            <div className="text-center">
              <div style={{ ...IT, fontSize: "12px", fontWeight: "700", color: regime.trend_alignment === "Aligned" ? "#22C55E" : "#F59E0B" }}>{regime.trend_alignment}</div>
              <div style={LB}>TREND</div>
            </div>
            <div className="text-center">
              <div style={{ ...IT, fontSize: "12px", fontWeight: "700", color: regime.fragility === "Resilient" ? "#22C55E" : regime.fragility === "Fragile" ? "#EF4444" : "#94A3B8" }}>{regime.fragility}</div>
              <div style={LB}>FRAGILITY</div>
            </div>
          </div>
        </OctaPanel>
      </div>

      {/* Top positions intelligence */}
      {topPos.length > 0 && (
        <OctaPanel delay={0.25} className="p-5">
          <div style={{ ...LB, marginBottom: "10px" }}>TOP POSITIONS — IMPACT ANALYSIS</div>
          <div className="space-y-1">
            {topPos.map((p, i) => {
              const isPnlPos = p.pnl >= 0;
              const pnlBar = Math.min(Math.abs(p.pnl) / (Math.max(...topPos.map(x => Math.abs(x.pnl)), 1)) * 100, 100);
              return (
                <div key={i} className="flex items-center gap-3 px-3 py-2.5" style={{ clipPath: CLIP_6, background: "rgba(255,255,255,0.01)", borderBottom: "1px solid rgba(255,255,255,0.02)" }}>
                  <span style={{ ...IT, fontSize: "10px", fontWeight: "700", color: "#E2E8F0", width: "55px" }}>{cleanSym(p.symbol)}</span>
                  <span style={{ ...IT, fontSize: "9px", fontWeight: "600", width: "40px", color: p.side === "LONG" ? "#22C55E" : "#EF4444" }}>{p.side}</span>
                  <span style={{ ...MN, fontSize: "9px", color: "#38BDF8", width: "28px" }}>{p.leverage}x</span>
                  {/* PnL impact bar */}
                  <div className="flex-1 h-1.5 overflow-hidden" style={{ clipPath: CLIP_6, background: "rgba(255,255,255,0.03)" }}>
                    <motion.div initial={{ width: 0 }} animate={{ width: `${pnlBar}%` }} transition={{ duration: 0.6, delay: i * 0.05 }}
                      style={{ height: "100%", background: isPnlPos ? "#22C55E" : "#EF4444", boxShadow: `0 0 4px ${isPnlPos ? "#22C55E20" : "#EF444420"}` }} />
                  </div>
                  <span style={{ ...MN, fontSize: "10px", fontWeight: "600", color: isPnlPos ? "#22C55E" : "#EF4444", width: "60px", textAlign: "right" }}>
                    {isPnlPos ? "+" : ""}{p.pnl.toFixed(2)}
                  </span>
                </div>
              );
            })}
          </div>
        </OctaPanel>
      )}
    </div>
  );
}

// ─── Section Header ────────────────────────────────────────────────
function SectionHeader({ label, count }) {
  return (
    <div className="flex items-center gap-3 mb-4">
      <div style={{ fontFamily: "'Inter Tight', sans-serif", fontSize: "10px", fontWeight: "600", letterSpacing: "0.18em", color: "#475569", textTransform: "uppercase" }}>{label}</div>
      <div style={{ flex: 1, height: "1px", background: "rgba(255,255,255,0.04)" }} />
      {count !== undefined && <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: "9px", fontWeight: "500", color: "#334155", letterSpacing: "0.1em" }}>{count}</div>}
    </div>
  );
}

// ─── Platform Workspace Page ───────────────────────────────────────
export default function PlatformWorkspace() {
  const { platformId } = useParams();
  const navigate = useNavigate();
  const [workspace, setWorkspace] = useState(null);
  const [summary, setSummary] = useState(null);
  const [selectedPos, setSelectedPos] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("positions");
  const [wsToken, setWsToken] = useState(null);

  useEffect(() => {
    axios.get(`${API}/auth/ws-token`).then(r => setWsToken(r.data.token)).catch(() => {});
  }, []);

  const { connected: wsConnected, lastMessage } = usePortfolioWS(wsToken);

  const fetchData = useCallback(async () => {
    try {
      const [wsRes, sumRes] = await Promise.all([
        axios.get(`${API}/platforms/${platformId}/workspace`),
        axios.get(`${API}/dashboard/summary`),
      ]);
      setWorkspace(wsRes.data);
      setSummary(sumRes.data);
    } catch (e) {
      console.error(e);
      if (e.response?.status === 404) navigate("/");
    }
    finally { setLoading(false); }
  }, [platformId, navigate]);

  useEffect(() => {
    fetchData();
    const iv = setInterval(fetchData, 30000);
    return () => clearInterval(iv);
  }, [fetchData]);

  // Refresh when WS sends a sync_update for this platform
  useEffect(() => {
    if (lastMessage?.type === "sync_update" && lastMessage?.data?.platform_id === platformId) {
      fetchData();
    }
  }, [lastMessage, platformId, fetchData]);

  if (loading) {
    return (
      <Layout summary={summary}>
        <div className="p-7">
          <div className="animate-pulse space-y-4">
            <div className="h-8 w-48 bg-white/[0.03]" />
            <div className="grid grid-cols-4 gap-5">{[1,2,3,4].map(i => <div key={i} className="h-24 octa-clip bg-[#111318]" />)}</div>
            <div className="grid grid-cols-3 gap-5">{[1,2,3].map(i => <div key={i} className="h-52 octa-clip bg-[#111318]" />)}</div>
          </div>
        </div>
      </Layout>
    );
  }

  if (!workspace) return null;

  const { platform, positions, orders, metrics } = workspace;
  const isPnlPositive = metrics.total_pnl >= 0;
  const marginLevel = metrics.margin_used > 80 ? "critical" : metrics.margin_used > 50 ? "elevated" : metrics.margin_used > 25 ? "moderate" : "low";

  const tabs = [
    { id: "positions", label: "Positions", count: positions.length },
    { id: "orders", label: "Orders", count: orders.length },
    { id: "risk", label: "Risk" },
    { id: "intelligence", label: "Intelligence" },
  ];

  return (
    <Layout summary={summary}>
      <div className="flex h-full">
        <div className="flex-1 overflow-auto p-7" data-testid="platform-workspace">
          {/* Back + Platform header */}
          <div className="flex items-center gap-4 mb-6">
            <button onClick={() => navigate("/")} data-testid="back-to-wall-btn" className="text-[#475569] hover:text-white transition-colors">
              <ArrowLeft size={18} />
            </button>
            <div>
              <h1 style={{ fontFamily: "'Inter Tight', sans-serif", fontWeight: "700", color: "#E2E8F0", fontSize: "16px", letterSpacing: "0.06em" }}>
                {platform.name}
              </h1>
              <div style={{ fontFamily: "'Inter Tight', sans-serif", fontSize: "10px", color: "#475569", letterSpacing: "0.1em", fontWeight: "500", marginTop: "2px" }}>
                {platform.connector_type?.toUpperCase()} · {platform.mode === "trade_enabled" ? "TRADE ENABLED" : "READ ONLY"}
              </div>
            </div>
            <div className="ml-auto flex items-center gap-3">
              {/* WS live indicator */}
              <div className="flex items-center gap-1.5" data-testid="workspace-ws-status">
                {wsConnected ? (
                  <>
                    <div className="w-1.5 h-1.5 rounded-full bg-[#22C55E] animate-pulse" />
                    <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: "8px", color: "#22C55E", letterSpacing: "0.08em" }}>LIVE</span>
                  </>
                ) : (
                  <>
                    <div className="w-1.5 h-1.5 rounded-full bg-[#475569]" />
                    <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: "8px", color: "#475569", letterSpacing: "0.08em" }}>POLLING</span>
                  </>
                )}
              </div>
              <button onClick={fetchData} data-testid="refresh-workspace-btn"
                className="flex items-center gap-2 transition-all duration-150 hover:brightness-125"
                style={{ clipPath: "polygon(6px 0%, calc(100% - 6px) 0%, 100% 6px, 100% calc(100% - 6px), calc(100% - 6px) 100%, 6px 100%, 0% calc(100% - 6px), 0% 6px)", background: "rgba(255,255,255,0.025)", border: "1px solid rgba(255,255,255,0.06)", color: "#475569", padding: "6px 14px", fontFamily: "'Inter Tight', sans-serif", fontSize: "9px", letterSpacing: "0.12em", fontWeight: "600" }}>
                <RefreshCw size={11} /> REFRESH
              </button>
            </div>
          </div>

          {/* Metrics row */}
          <div className="grid grid-cols-4 gap-5 mb-7">
            {[
              { label: "EQUITY", value: `$${metrics.equity.toLocaleString("en-US", { minimumFractionDigits: 2 })}`, color: "#E2E8F0" },
              { label: "PNL", value: `${isPnlPositive ? "+" : ""}${metrics.total_pnl.toFixed(2)}`, color: isPnlPositive ? "#22C55E" : "#EF4444" },
              { label: "MARGIN USED", value: `${metrics.margin_used}%`, color: RISK_COLORS[marginLevel] },
              { label: "POSITIONS", value: metrics.positions_count.toString(), color: "#38BDF8" },
            ].map((m, i) => (
              <OctaPanel key={m.label} delay={i * 0.06} className="p-4">
                <div style={{ fontFamily: "'Inter Tight', sans-serif", fontSize: "9px", letterSpacing: "0.14em", color: "#475569", fontWeight: "500", textTransform: "uppercase", marginBottom: "6px" }}>{m.label}</div>
                <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: "20px", fontWeight: "700", color: m.color, fontVariantNumeric: "tabular-nums" }}>{m.value}</div>
              </OctaPanel>
            ))}
          </div>

          {/* Tabs */}
          <div className="flex items-center gap-1 mb-6">
            {tabs.map(tab => (
              <button key={tab.id} onClick={() => setActiveTab(tab.id)} data-testid={`tab-${tab.id}`}
                className="px-4 py-2 octa-clip-sm transition-all"
                style={{
                  fontFamily: "'Inter Tight', sans-serif", fontWeight: "600", fontSize: "10px", letterSpacing: "0.1em", textTransform: "uppercase",
                  background: activeTab === tab.id ? "rgba(37,99,235,0.08)" : "transparent",
                  color: activeTab === tab.id ? "#2563EB" : "#475569",
                  border: `1px solid ${activeTab === tab.id ? "rgba(37,99,235,0.2)" : "rgba(255,255,255,0.04)"}`,
                }}>
                {tab.label}{tab.count !== undefined ? ` (${tab.count})` : ""}
              </button>
            ))}
          </div>

          {/* Positions tab */}
          {activeTab === "positions" && (
            <>
              <SectionHeader label="Open Positions" count={`${positions.length} ACTIVE`} />
              {positions.length === 0 ? (
                <div className="text-center py-16">
                  <p style={{ fontFamily: "'IBM Plex Sans', sans-serif", fontSize: "12px", color: "#475569" }}>No active positions on this platform.</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                  {positions.map((pos, i) => (
                    <PositionCard key={pos._id} pos={pos} isSelected={selectedPos?._id === pos._id} onSelect={setSelectedPos} delay={i * 0.06} />
                  ))}
                </div>
              )}
            </>
          )}

          {/* Orders tab */}
          {activeTab === "orders" && (
            <>
              <SectionHeader label="Recent Orders" count={`${orders.length} ORDERS`} />
              <OctaPanel delay={0.1} className="overflow-hidden">
                {orders.length === 0 ? (
                  <div className="p-12 text-center">
                    <p style={{ fontFamily: "'IBM Plex Sans', sans-serif", fontSize: "12px", color: "#475569" }}>No orders found.</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <div className="grid grid-cols-7 gap-4 px-5 py-3" style={{ borderBottom: "1px solid rgba(255,255,255,0.04)" }}>
                      {["Symbol", "Side", "Type", "Qty", "Price", "Status", "Time"].map(h => (
                        <div key={h} style={{ fontFamily: "'Inter Tight', sans-serif", fontSize: "9px", letterSpacing: "0.14em", color: "#334155", fontWeight: "600", textTransform: "uppercase" }}>{h}</div>
                      ))}
                    </div>
                    {orders.map((order, i) => (
                      <div key={`${order.order_id}-${i}`} className="grid grid-cols-7 gap-4 px-5 py-3 hover:bg-white/[0.015] transition-colors" style={{ borderBottom: "1px solid rgba(255,255,255,0.02)", background: i % 2 === 0 ? "transparent" : "rgba(255,255,255,0.008)" }}>
                        <div style={{ fontFamily: "'Inter Tight', sans-serif", fontWeight: "600", color: "#E2E8F0", fontSize: "12px" }}>{order.symbol}</div>
                        <div className="flex items-center gap-1" style={{ fontFamily: "'Inter Tight', sans-serif", fontWeight: "600", fontSize: "11px", color: order.side === "BUY" ? "#22C55E" : "#EF4444" }}>
                          {order.side === "BUY" ? <TrendingUp size={10} /> : <TrendingDown size={10} />} {order.side}
                        </div>
                        <div style={{ fontFamily: "'IBM Plex Sans', sans-serif", fontSize: "11px", color: "#94A3B8" }}>{order.type}</div>
                        <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: "11px", color: "#94A3B8", fontVariantNumeric: "tabular-nums" }}>{order.quantity}</div>
                        <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: "11px", color: "#E2E8F0", fontVariantNumeric: "tabular-nums" }}>{order.price > 0 ? `$${order.price.toLocaleString()}` : "Market"}</div>
                        <div><span className="octa-clip-sm px-2 py-0.5" style={{ fontFamily: "'Inter Tight', sans-serif", fontWeight: "600", fontSize: "9px", color: order.status === "filled" ? "#22C55E" : "#38BDF8", background: order.status === "filled" ? "rgba(34,197,94,0.08)" : "rgba(56,189,248,0.08)" }}>{order.status}</span></div>
                        <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: "10px", color: "#475569", fontVariantNumeric: "tabular-nums" }}>{order.created_at ? new Date(order.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : "--"}</div>
                      </div>
                    ))}
                  </div>
                )}
              </OctaPanel>
            </>
          )}

          {/* Intelligence tab */}
          {activeTab === "intelligence" && (
            <PlatformIntelligenceTab platformId={platformId} />
          )}

          {/* Risk tab */}
          {activeTab === "risk" && (
            <>
              <SectionHeader label="Risk Analysis" />
              <div className="space-y-5">
                {/* Margin bar */}
                <OctaPanel delay={0.1} className="p-5">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="flex items-center gap-2" style={{ fontFamily: "'Inter Tight', sans-serif", fontWeight: "600", color: "#E2E8F0", fontSize: "12px" }}>
                      <ShieldAlert size={14} /> Margin Utilization
                    </h3>
                    <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: "12px", fontWeight: "600", color: RISK_COLORS[marginLevel], fontVariantNumeric: "tabular-nums" }}>{metrics.margin_used}%</span>
                  </div>
                  <div className="h-1.5 octa-clip-sm overflow-hidden" style={{ background: "rgba(255,255,255,0.03)" }}>
                    <motion.div initial={{ width: 0 }} animate={{ width: `${metrics.margin_used}%` }} transition={{ duration: 1 }} className="h-full" style={{ background: RISK_COLORS[marginLevel] }} />
                  </div>
                  {marginLevel === "critical" && (
                    <div className="flex items-center gap-2 mt-3" style={{ fontFamily: "'IBM Plex Sans', sans-serif", fontSize: "11px", color: "#EF4444" }}>
                      <AlertTriangle size={12} /> High margin — reduce exposure
                    </div>
                  )}
                </OctaPanel>

                {/* Position risk table */}
                <OctaPanel delay={0.15} className="p-5">
                  <h3 style={{ fontFamily: "'Inter Tight', sans-serif", fontWeight: "600", color: "#E2E8F0", fontSize: "12px", marginBottom: "16px" }}>Position Risk</h3>
                  {positions.length === 0 ? (
                    <p style={{ fontFamily: "'IBM Plex Sans', sans-serif", fontSize: "12px", color: "#475569", textAlign: "center", padding: "32px 0" }}>No open positions</p>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full" data-testid="workspace-risk-table">
                        <thead>
                          <tr style={{ borderBottom: "1px solid rgba(255,255,255,0.04)" }}>
                            {["Symbol", "Side", "Size", "Entry", "Mark", "Lev", "PnL", "Liq"].map(h => (
                              <th key={h} className="text-left pb-3 pr-4" style={{ fontFamily: "'Inter Tight', sans-serif", fontSize: "9px", letterSpacing: "0.14em", color: "#334155", fontWeight: "600", textTransform: "uppercase" }}>{h}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {positions.map(pos => (
                            <tr key={pos._id} style={{ borderBottom: "1px solid rgba(255,255,255,0.02)" }} className="hover:bg-white/[0.02]">
                              <td className="py-3 pr-4" style={{ fontFamily: "'Inter Tight', sans-serif", fontWeight: "600", color: "#E2E8F0", fontSize: "12px" }}>{cleanSym(pos.symbol)}</td>
                              <td className="py-3 pr-4" style={{ fontFamily: "'Inter Tight', sans-serif", fontWeight: "600", fontSize: "11px", color: pos.side === "LONG" ? "#22C55E" : "#EF4444" }}>{pos.side}</td>
                              <td className="py-3 pr-4" style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: "11px", color: "#94A3B8", fontVariantNumeric: "tabular-nums" }}>{pos.quantity}</td>
                              <td className="py-3 pr-4" style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: "11px", color: "#94A3B8", fontVariantNumeric: "tabular-nums" }}>${pos.entry_price?.toLocaleString()}</td>
                              <td className="py-3 pr-4" style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: "11px", color: "#E2E8F0", fontVariantNumeric: "tabular-nums" }}>${pos.mark_price?.toLocaleString()}</td>
                              <td className="py-3 pr-4" style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: "11px", color: "#38BDF8", fontVariantNumeric: "tabular-nums" }}>{pos.leverage}x</td>
                              <td className="py-3 pr-4" style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: "11px", fontWeight: "600", color: pos.pnl >= 0 ? "#22C55E" : "#EF4444", fontVariantNumeric: "tabular-nums" }}>{pos.pnl >= 0 ? "+" : ""}{pos.pnl?.toFixed(2)}</td>
                              <td className="py-3 pr-4" style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: "11px", color: "#F59E0B", fontVariantNumeric: "tabular-nums" }}>${pos.liquidation_price?.toLocaleString()}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </OctaPanel>
              </div>
            </>
          )}
        </div>

        {/* Right Detail Panel */}
        <AnimatePresence>
          {selectedPos && (
            <PositionDetailPanel position={selectedPos} onClose={() => setSelectedPos(null)} />
          )}
        </AnimatePresence>
      </div>
    </Layout>
  );
}

const RISK_COLORS = {
  low: "#22C55E",
  moderate: "#38BDF8",
  elevated: "#F59E0B",
  critical: "#EF4444",
};
